/**
 * Pure helpers for discount presets: client-side validation that mirrors the
 * backend rules (so the user sees the error before a 400), the name ordering
 * the backend uses for the presets list, and the quick-discount ordering.
 * Shared by Settings, the transaction apply-discount modal and the quick
 * discount shortcuts.
 */
import {
  DiscountPreset,
  DiscountType,
  MAX_DISCOUNT_PERCENTAGE,
  MAX_DISCOUNT_PRESET_NAME_LENGTH,
  MAX_QUICK_DISCOUNTS,
} from "../types/discounts";

const hasControlCharacter = (value: string): boolean =>
  Array.from(value).some((char) => {
    const code = char.charCodeAt(0);
    return code < 32 || code === 127;
  });

/** True when `value` has no more than two decimal places (cents precision). */
export const hasAtMostTwoDecimals = (value: number): boolean =>
  Math.abs(Math.round(value * 100) - value * 100) < 1e-6;

/**
 * Validation message for a discount name, or null when valid. Mirrors the
 * backend: trimmed, 1–100 characters, no control characters.
 */
export function validateDiscountName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Name is required";
  if (trimmed.length > MAX_DISCOUNT_PRESET_NAME_LENGTH) {
    return `Name must be ${MAX_DISCOUNT_PRESET_NAME_LENGTH} characters or less`;
  }
  if (hasControlCharacter(trimmed)) {
    return "Name contains characters that aren't allowed";
  }
  return null;
}

/**
 * Validation message for a discount amount, or null when valid. Mirrors the
 * backend: > 0, at most two decimals, and ≤ 100 for a percentage. Accepts the
 * `number | string` a Mantine NumberInput produces ("" while empty).
 */
export function validateDiscountAmount(
  type: DiscountType,
  amount: number | string | null | undefined,
): string | null {
  if (amount === "" || amount === null || amount === undefined) {
    return "Amount is required";
  }
  const value = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(value)) return "Amount is required";
  if (value <= 0) return "Amount must be greater than 0";
  if (!hasAtMostTwoDecimals(value)) {
    return "Amount can have at most 2 decimal places";
  }
  if (type === "Percentage" && value > MAX_DISCOUNT_PERCENTAGE) {
    return `Percentage cannot exceed ${MAX_DISCOUNT_PERCENTAGE}%`;
  }
  return null;
}

/** Case-insensitive name comparator — the backend's list ordering. */
export const compareDiscountPresetNames = (
  a: Pick<DiscountPreset, "name">,
  b: Pick<DiscountPreset, "name">,
): number => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/** A new array of presets ordered by name, case-insensitively. */
export const sortDiscountPresets = (
  presets: DiscountPreset[],
): DiscountPreset[] => [...presets].sort(compareDiscountPresetNames);

/**
 * The preset whose name matches `name` (trimmed, case-insensitive), if any —
 * the same uniqueness rule the backend enforces with a 409.
 */
export function findDiscountPresetByName(
  presets: DiscountPreset[],
  name: string,
): DiscountPreset | undefined {
  const needle = name.trim().toLocaleLowerCase();
  if (!needle) return undefined;
  return presets.find(
    (preset) => preset.name.trim().toLocaleLowerCase() === needle,
  );
}

/** The backend's 409: an active preset with that name already exists here. */
export const isDuplicatePresetNameError = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 409;

/**
 * The location's quick discounts in display order (by `quick_position`; the
 * backend can leave a gap, e.g. 1, 3, after one is deleted), capped at
 * MAX_QUICK_DISCOUNTS.
 */
export function getQuickDiscounts(presets: DiscountPreset[]): DiscountPreset[] {
  return presets
    .filter((preset) => preset.quick_position != null)
    .sort((a, b) => (a.quick_position ?? 0) - (b.quick_position ?? 0))
    .slice(0, MAX_QUICK_DISCOUNTS);
}

/**
 * `presets` with `quick_position` renumbered 1..n from `orderedIds` (every
 * other preset cleared) — the state the backend returns after a quick
 * discounts PUT, for applying it optimistically.
 */
export function withQuickDiscountOrder(
  presets: DiscountPreset[],
  orderedIds: string[],
): DiscountPreset[] {
  return presets.map((preset) => {
    const index = orderedIds.indexOf(preset.id);
    return { ...preset, quick_position: index === -1 ? null : index + 1 };
  });
}

/**
 * `orderedIds` with `fromId` moved to `toId`'s slot (a drag-reorder drop), or
 * null when the drop changes nothing (same slot, or an unknown id).
 */
export function moveQuickDiscount(
  orderedIds: string[],
  fromId: string,
  toId: string,
): string[] | null {
  const from = orderedIds.indexOf(fromId);
  const to = orderedIds.indexOf(toId);
  if (from === -1 || to === -1 || from === to) return null;
  const next = [...orderedIds];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
}
